import React, { Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { StartOfWeekFormatSetting, TimeFormatSetting } from '../../../../models/graphql';
import { BreakpointsEnum } from '../../../../constants/breakpoints';
import { useBreakpoints } from '../../../../hooks/useBreakpoints';
import { PostingPlanListView } from '../PostingPlanListView';
import classes from './PostingPlanCalendar.module.scss';
import { PostingPlanContext } from '../../../../providers/PostingPlanProvider';
import { Calendar } from '../../../../components/containers/Calendar';
import { PostingPlanHelpers } from '../postingPlanHelpers';
import { CalendarCellTemplate } from '../CalendarCellTemplate';
import { TimeslotDragLayer } from '../TimeslotDragLayer';
import { CSSTransition } from 'react-transition-group';
import { useSettings } from 'hooks/useSettings';
import { useDidMount } from '../../../../hooks/useDidMount';

export type PostingPlanViewProps = {
  timeFormat: TimeFormatSetting;
  setLoading: Dispatch<SetStateAction<boolean>>;
};

type TimeslotPositionsData = {
  first: number;
  last: number;
  timeslot: boolean;
};

export const PostingPlanCalendarView = (props: PostingPlanViewProps): JSX.Element => {
  const { timeslots, scrollBackPosition, setScrollBackPosition } = useContext(PostingPlanContext);
  const [, setReloadCalendar] = useState({});
  const breakpoint = useBreakpoints();
  const [showMobileView, setShowMobileView] = useState(breakpoint === BreakpointsEnum.small);
  const [shouldScroll, setShouldScroll] = useState<boolean>(true);
  const [showMore, setShowMore] = useState<boolean>(false);
  const timeslotPositionsRef = useRef<TimeslotPositionsData>({ first: -1, last: -1, timeslot: false });
  const showMoreRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const { selectedOrgSettings } = useSettings();
  const dayStartOfWeek =
    selectedOrgSettings?.startOfWeekFormat === StartOfWeekFormatSetting.SUNDAY
      ? StartOfWeekFormatSetting.SUNDAY
      : StartOfWeekFormatSetting.MONDAY;
  const hasScrollBackPosition = !!scrollBackPosition.top || !!scrollBackPosition.left;

  const onScroll: EventListener = useCallback(
    (e: Event) => {
      const parent = e.currentTarget as HTMLDivElement;

      const scrollX = parent.scrollLeft;
      const scrollY = parent.scrollTop;

      setScrollBackPosition({ left: scrollX, top: scrollY });

      const parentOffset = parent.getBoundingClientRect();
      const rows = parent.querySelectorAll('tbody tr') as NodeListOf<HTMLTableRowElement>;
      if (timeslotPositionsRef.current.last > -1) {
        const lastRow = rows.item(timeslotPositionsRef.current.last);
        const lastCardOffset = lastRow.querySelector('td:last-child')?.lastElementChild?.getBoundingClientRect();
        const halfHeightLastCard = lastCardOffset ? lastCardOffset.height / 2 : 0;

        if (
          timeslotPositionsRef.current.timeslot &&
          lastCardOffset &&
          lastCardOffset.top + halfHeightLastCard > parentOffset.bottom
        ) {
          setShowMore(true);
        } else {
          setShowMore(false);
        }
      }
    },
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
    [timeslotPositionsRef.current, showMore]
  );

  const renderCallback = useCallback((elem: HTMLTableElement | null) => {
    tableRef.current = elem;
  }, []);

  useEffect(() => {
    //scroll to the first visible timeslot
    if (!hasScrollBackPosition && shouldScroll && !showMobileView && tableRef.current) {
      const tableElem = tableRef.current;
      const { rowIndex, colIndex, timeslot } = PostingPlanHelpers.getTimeslotCoordinates(timeslots, 'FIRST');
      const { rowIndex: lastIndex } = PostingPlanHelpers.getTimeslotCoordinates(timeslots, 'LAST');

      timeslotPositionsRef.current = { ...timeslotPositionsRef.current, first: rowIndex, last: lastIndex, timeslot };

      if (timeslot) {
        setTimeout(() => {
          // trigger scroll to check for the label
          const parentHolder = tableElem.parentElement as HTMLDivElement;
          parentHolder.scrollTo({
            top: 1,
            behavior: 'smooth'
          });

          PostingPlanHelpers.scrollToTimeslot(tableRef, rowIndex, colIndex);
          setShouldScroll(false);
        }, 1);
      }
    }

    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [timeslots, showMobileView, shouldScroll, tableRef]);

  // show appropriate view for posting plan
  useEffect(() => {
    if (breakpoint === BreakpointsEnum.small) {
      if (!showMobileView) {
        setShowMobileView(true);
      }
      if (!shouldScroll) {
        setShouldScroll(true);
      }
    } else {
      if (showMobileView) {
        setReloadCalendar({});
        setShowMobileView(false);
      }
    }
    // TODO-FIX-TS-WARNINGS
    // eslint-disable-next-line
  }, [breakpoint]);

  useDidMount(() => {
    if (hasScrollBackPosition) {
      const parent = tableRef.current?.parentElement;

      if (!parent) return;

      parent.scrollTo({
        ...scrollBackPosition,
        behavior: 'smooth'
      });
    }
  });

  return showMobileView ? (
    <div className={classes.ListViewWrapper}>
      <PostingPlanListView events={timeslots} setLoading={props.setLoading} startDayOfWeek={dayStartOfWeek} />
    </div>
  ) : (
    <>
      <TimeslotDragLayer timeFormat={props.timeFormat} startDayOfWeek={dayStartOfWeek} />
      <Calendar
        onScroll={onScroll}
        renderCallback={renderCallback}
        fixedHeader={true}
        data={timeslots}
        headerNames={PostingPlanHelpers.getHeaderNames(dayStartOfWeek)}
        startDayOfWeek={dayStartOfWeek}
        cellTemplate={CalendarCellTemplate}
        timeFormat={props.timeFormat}
      />
      <CSSTransition in={showMore} timeout={300} unmountOnExit classNames={'fade'} nodeRef={showMoreRef}>
        <div className={classes.ShowMore} ref={showMoreRef}>
          There are more visible timeslots
        </div>
      </CSSTransition>
    </>
  );
};
